import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AlertTriangle, Plus, Loader2, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Severity = 'critical' | 'high' | 'medium' | 'low';
type IncidentStatus = 'open' | 'investigating' | 'resolved' | 'closed';

interface Incident {
  id: string;
  title: string;
  description: string;
  severity: Severity;
  status: IncidentStatus;
  affected_systems: string;
  created_at: string;
  updated_at: string;
}

interface IncidentsResponse {
  incidents: Incident[];
  stats: {
    total: number;
    open: number;
    investigating: number;
    resolved: number;
  };
}

interface CreateIncidentPayload {
  title: string;
  description: string;
  severity: Severity;
  affected_systems: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function severityBadge(severity: Severity) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (severity) {
    case 'critical':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Critical</span>;
    case 'high':
      return <span className={`${base} bg-orange-500/15 text-orange-500`}>High</span>;
    case 'medium':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Medium</span>;
    case 'low':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Low</span>;
  }
}

function statusBadge(status: IncidentStatus) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'open':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Open</span>;
    case 'investigating':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Investigating</span>;
    case 'resolved':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Resolved</span>;
    case 'closed':
      return <span className={`${base} bg-muted text-muted-foreground`}>Closed</span>;
  }
}

const STATUS_TRANSITIONS: Record<IncidentStatus, IncidentStatus | null> = {
  open: 'investigating',
  investigating: 'resolved',
  resolved: 'closed',
  closed: null,
};

const STATUS_ACTION_LABELS: Record<IncidentStatus, string> = {
  open: 'Start Investigation',
  investigating: 'Mark Resolved',
  resolved: 'Close',
  closed: '',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function IncidentsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateIncidentPayload>({
    title: '',
    description: '',
    severity: 'medium',
    affected_systems: '',
  });

  const incidentsQuery = useQuery<IncidentsResponse>({
    queryKey: ['governance', 'incidents'],
    queryFn: () => api<IncidentsResponse>('governance/incidents'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateIncidentPayload) =>
      api('governance/incidents', { body: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'incidents'] });
      setShowForm(false);
      setForm({ title: '', description: '', severity: 'medium', affected_systems: '' });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: IncidentStatus }) =>
      api(`governance/incidents`, { method: 'PUT', body: { id, status } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'incidents'] });
    },
  });

  const stats = incidentsQuery.data?.stats;
  const incidents = incidentsQuery.data?.incidents ?? [];

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) return;
    createMutation.mutate(form);
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <AlertTriangle className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">AI Incidents</h1>
            <p className="text-muted-foreground mt-1">
              Report and track AI-related incidents through investigation and resolution.
            </p>
          </div>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 flex-shrink-0"
          >
            <Plus className="h-4 w-4" />
            Report Incident
          </button>
        )}
      </div>

      {/* Stats */}
      {incidentsQuery.isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Total Incidents</p>
            <p className="text-2xl font-bold text-foreground">{stats?.total ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Open</p>
            <p className="text-2xl font-bold text-red-500">{stats?.open ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Investigating</p>
            <p className="text-2xl font-bold text-yellow-500">{stats?.investigating ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Resolved</p>
            <p className="text-2xl font-bold text-green-500">{stats?.resolved ?? 0}</p>
          </div>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Report New Incident</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Title</label>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Brief incident title"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Severity</label>
              <select
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value as Severity })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Describe what happened, impact, and any immediate actions taken..."
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-y"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Affected Systems</label>
            <input
              value={form.affected_systems}
              onChange={(e) => setForm({ ...form, affected_systems: e.target.value })}
              placeholder="e.g. Chat API, Content Generation Pipeline"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {createMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create incident.'}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={createMutation.isPending || !form.title.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {createMutation.isPending ? 'Submitting...' : 'Submit Incident'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Incidents list */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">All Incidents</h2>

        {incidentsQuery.isLoading && (
          <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading incidents...</span>
          </div>
        )}

        {incidentsQuery.isSuccess && incidents.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
            No incidents reported. Click "Report Incident" to log a new one.
          </div>
        )}

        {incidentsQuery.isSuccess && incidents.length > 0 && (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <span className="flex-1">Title</span>
              <span className="w-20">Severity</span>
              <span className="w-24">Status</span>
              <span className="w-36">Reported</span>
              <span className="w-32">Action</span>
            </div>
            {incidents.map((incident) => {
              const nextStatus = STATUS_TRANSITIONS[incident.status];
              return (
                <div
                  key={incident.id}
                  className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-accent/50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{incident.title}</p>
                    {incident.affected_systems && (
                      <p className="text-xs text-muted-foreground truncate">{incident.affected_systems}</p>
                    )}
                  </div>
                  <span className="w-20">{severityBadge(incident.severity)}</span>
                  <span className="w-24">{statusBadge(incident.status)}</span>
                  <span className="w-36 text-sm text-muted-foreground">{formatDate(incident.created_at)}</span>
                  <span className="w-32">
                    {nextStatus && (
                      <button
                        type="button"
                        disabled={updateStatusMutation.isPending}
                        onClick={() => updateStatusMutation.mutate({ id: incident.id, status: nextStatus })}
                        className="px-3 py-1 rounded-md text-xs font-medium border border-border text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                      >
                        {STATUS_ACTION_LABELS[incident.status]}
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
